import { takeLatest, put, select, call, all } from 'redux-saga/effects';
import { readCookie, parseBoolean } from '@tcp/core/src/utils';
import {
  getFlipSwitchState,
  getUnbxdDRUrlState,
} from '@tcp/core/src/reduxStore/selectors/session.selectors';
import { setHorizontalCategoryProducts } from '@tcp/core/src/components/features/browse/HorizontalCategory/container/HorizontalCategory.actions';
import logger from '@tcp/core/src/utils/loggerInstance';
import ShopThisStoreCategories from '@tcp/core/src/services/abstractors/shopThisStore';
import { SHOP_THIS_STORE_ACTIONS } from './ShopThisStore.constants';
import {
  getBreadCrumbs,
  getNavigation,
  getShopThisStoreL1categorySelection,
} from './ShopThisStore.selectors';
import {
  getL2Categories,
  setGetL1CategoriesLoading,
  setGetL2CategoriesLoading,
  setIsDefaultCategoryLoaded,
  setL1CategorySelection,
  setL1CategorySelectionLoaded,
  setL2CategorySelection,
  setL2CategorySelectionLoaded,
  setLoadedInShopThisStore,
  setSelectedL1CategoryContent,
  setSelectedL2CategoryGroupID,
  setSelectedStoreL1Category,
  setSelectedStoreL2Category,
} from './ShopThisStore.actions';
import {
  setConsolidatedPlpRes,
  setCurrNavIds,
  setHorizontalPlpFlag,
} from '../../CategoryListingPage/container/CategoryListingPage.actions';
import { fetchHorizontalCategoryProducts } from '../../HorizontalCategory/container/HorizontalCategory.saga';
import { getSelectedStoreL1Categories, getSelectedStoreL2Categories } from './ShopThisStore.utils';

export function* getUnbxdXappConfigs() {
  const isFlipUnbxdDrUrl = readCookie('isFlipUnbxdDrUrl');
  const isFlipUrlSwitch = yield select(getFlipSwitchState);
  const drURLpath = yield select(getUnbxdDRUrlState);
  return {
    isFlipURL: isFlipUrlSwitch || parseBoolean(isFlipUnbxdDrUrl),
    drURLpath,
  };
}

export function* handleDefaultCategorySelection({
  category,
  categoryAction,
  callbackAction,
  callbackActionPayload,
}) {
  try {
    if (category) {
      yield put(categoryAction(category));
      yield put(callbackAction(callbackActionPayload));
    }
  } catch (error) {
    logger.error({
      error: `Error in shop this store saga - ${error}`,
      extraData: {
        component: 'ShopThisStoreSaga',
        methodName: 'handleDefaultCategorySelection',
      },
    });
  }
}

export function handleL2CategoryBasedOnL1Category({ currentSelectedL2, l2Categories }) {
  try {
    return l2Categories?.find((category, idx) => {
      if (currentSelectedL2) {
        return category.label === currentSelectedL2;
      }
      return idx === 0;
    });
  } catch (error) {
    logger.error({
      error: `Error in handleL2CategoryBasedOnL1Category method of Shop this store saga - ${error}`,
      extraData: {
        component: 'ShopThisStoreSaga',
        methodName: 'handleL2CategoryBasedOnL1Category',
      },
    });
    return null;
  }
}

export function* fetchInitialCategory({ payload }) {
  try {
    const { cid, id } = payload;
    const activeCategoryIds = [id];
    const breadCrumbs = yield select(getBreadCrumbs(cid));
    yield put(setHorizontalCategoryProducts({}));
    yield put(setConsolidatedPlpRes({ breadCrumbs }));
    yield put(setCurrNavIds({ activeCategoryIds }));
    yield put(setLoadedInShopThisStore(true));
    yield put(setHorizontalPlpFlag(true));
    yield call(fetchHorizontalCategoryProducts, { payload });
  } catch (error) {
    logger.error({
      error: `Error in shop this store saga - ${error}`,
      extraData: {
        component: 'ShopThisStoreSaga',
        methodName: 'fetchInitialCategory',
      },
    });
  }
}

export function* fetchL1Categories({
  payload: { storeId = '', category: currentL1Category, categoryGrouping: currentL2Category },
}) {
  try {
    let defaultL1Category;
    let defaultL1CategoryContent;
    yield put(setGetL1CategoriesLoading({ isLoading: true }));
    yield put(setIsDefaultCategoryLoaded(false));
    yield put({ type: SHOP_THIS_STORE_ACTIONS.SET_L1_CATEGORIES, data: [] });
    const xappURLConfig = yield call(getUnbxdXappConfigs);
    const response = yield call(ShopThisStoreCategories.fetchL1Categories, {
      storeId,
      xappURLConfig,
    });
    yield put({ type: SHOP_THIS_STORE_ACTIONS.SET_L1_CATEGORIES, data: response });
    if (response) yield put(setGetL1CategoriesLoading({ isLoading: false }));
    if (response) yield put({ type: SHOP_THIS_STORE_ACTIONS.SET_L1_CATEGORIES_FETCHED });
    if (response) {
      const navigationData = yield select(getNavigation);
      const stsL1Category = getSelectedStoreL1Categories(navigationData, response);
      yield put(setSelectedStoreL1Category(stsL1Category));
      if (currentL1Category) {
        defaultL1Category = stsL1Category?.find(
          (l1Category) => l1Category?.categoryContent?.name === currentL1Category
        );
        defaultL1CategoryContent = defaultL1Category?.categoryContent;
      } else {
        [defaultL1Category] = stsL1Category;
        defaultL1CategoryContent = stsL1Category[0]?.categoryContent;
      }
      yield handleDefaultCategorySelection({
        category: defaultL1Category,
        categoryAction: setL1CategorySelection,
        callbackAction: setL1CategorySelectionLoaded,
        callbackActionPayload: true,
      });
      yield put(setSelectedL1CategoryContent(defaultL1CategoryContent));
      yield put(getL2Categories(storeId, defaultL1CategoryContent?.name, currentL2Category));
    }
  } catch (err) {
    yield put(setGetL1CategoriesLoading({ isLoading: false }));
    yield put({ type: SHOP_THIS_STORE_ACTIONS.SET_L1_CATEGORIES_FETCHED });
    logger.error({ error: err });
  }
}

export function* fetchL2Categories({
  payload: { storeId = '', categoryFilter = '', currentL2Category = '' },
}) {
  try {
    let defaultL2Category;
    let defaultL2CategoryGroupId;
    yield put(setGetL2CategoriesLoading({ isLoading: true }));
    yield put({ type: SHOP_THIS_STORE_ACTIONS.SET_L2_CATEGORIES, data: [] });
    const xappURLConfig = yield call(getUnbxdXappConfigs);
    const response = yield call(ShopThisStoreCategories.fetchL2Categories, {
      storeId,
      categoryFilter,
      xappURLConfig,
    });
    yield put({ type: SHOP_THIS_STORE_ACTIONS.SET_L2_CATEGORIES, data: response });
    yield put(setGetL2CategoriesLoading({ isLoading: false }));
    if (response) {
      const stsL1Category = yield select(getShopThisStoreL1categorySelection);
      const stsL2Categories = getSelectedStoreL2Categories(stsL1Category, response);
      const attemptedL2 = handleL2CategoryBasedOnL1Category({
        currentSelectedL2: currentL2Category,
        l2Categories: stsL2Categories,
      });
      if (attemptedL2) {
        defaultL2Category = attemptedL2;
        defaultL2CategoryGroupId = attemptedL2[0]?.items[0]?.categoryContent?.groupIdentifier;
      } else {
        [defaultL2Category] = stsL2Categories;
        defaultL2CategoryGroupId = stsL2Categories[0]?.items[0]?.categoryContent?.groupIdentifier;
      }
      yield put(setSelectedStoreL2Category(stsL2Categories));
      yield handleDefaultCategorySelection({
        category: defaultL2Category,
        categoryAction: setL2CategorySelection,
        callbackAction: setL2CategorySelectionLoaded,
        callbackActionPayload: true,
      });
      yield put(setSelectedL2CategoryGroupID(defaultL2CategoryGroupId));
      yield put(setIsDefaultCategoryLoaded(true));
    }
  } catch (err) {
    logger.error({ error: err });
  } finally {
    yield put(setGetL2CategoriesLoading({ isLoading: false }));
  }
}

export function* fetchFilteredL2AndL1Categories(action) {
  try {
    const { storeId, breadCrumbs } = action.payload;
    const l2Cat = breadCrumbs[1]?.displayName || '';
    const l3Cat = breadCrumbs[2]?.displayName || '';
    yield put({ type: SHOP_THIS_STORE_ACTIONS.SET_FILTERED_L2_AND_L1_CATEGORIES, data: [] });
    const xappURLConfig = yield call(getUnbxdXappConfigs);
    const l2l1ApiArray = [];
    if (l2Cat) {
      l2l1ApiArray.push(
        call(ShopThisStoreCategories.fetchL2Categories, {
          storeId,
          categoryFilter: l2Cat,
          xappURLConfig,
        })
      );
      if (l3Cat) {
        const categoryFilter = `${l2Cat}>${l3Cat}`;
        l2l1ApiArray.push(
          call(ShopThisStoreCategories.fetchL2Categories, {
            storeId,
            categoryFilter,
            xappURLConfig,
          })
        );
      }
      const l2l1Resp = yield all(l2l1ApiArray);
      let mainArray = [];
      if (l2l1Resp && l2l1Resp.length > 1) {
        mainArray = [...l2l1Resp[0], ...l2l1Resp[1]];
      } else {
        mainArray = [...l2l1Resp[0]];
      }
      const reducedArray = mainArray.filter((ele) => {
        return typeof ele === 'string';
      });
      yield put({
        type: SHOP_THIS_STORE_ACTIONS.SET_FILTERED_L2_AND_L1_CATEGORIES,
        data: reducedArray,
      });
    }
  } catch (err) {
    logger.error({ error: err });
  }
}

function* ShopThisStoreSaga() {
  yield takeLatest(SHOP_THIS_STORE_ACTIONS.FETCH_HLP_CATEGORY, fetchInitialCategory);
  yield takeLatest(SHOP_THIS_STORE_ACTIONS.FETCH_L1_CATEGORIES, fetchL1Categories);
  yield takeLatest(SHOP_THIS_STORE_ACTIONS.FETCH_L2_CATEGORIES, fetchL2Categories);
  yield takeLatest(
    SHOP_THIS_STORE_ACTIONS.FETCH_FILTERED_L2_AND_L1_CATEGORIES,
    fetchFilteredL2AndL1Categories
  );
}

export default ShopThisStoreSaga;
